import { Typography, useTheme } from '@forgeglobal/design-framework';
import { useStyles } from './style';
import InfoCard from '@tcs/common/components/molecules/InfoCard';
import usePromissoryNote from '@tcs/client-portal-common/hooks/usePromissoryNote';
import formatDate from '@tcs/common/utils/formatDate';
import { differenceInMonths, differenceInYears } from 'date-fns';

interface Props {
  title: string;
  accountNumber: string;
  assetCode: string;
}

const AssetInfoCard = ({ title, accountNumber, assetCode }: Props) => {
  const { loading: loadingPromissoryNote, data: promissoryNote } =
    usePromissoryNote(accountNumber, assetCode);

  const classes = useStyles();

  // make sure we don't display negative
  const theme = useTheme();

  if (!promissoryNote) return;

  const issueDate = new Date(promissoryNote?.issueDate);

  const displayMaturityDate = promissoryNote.extendedMaturityDate
    ? promissoryNote.extendedMaturityDate
    : promissoryNote.maturityDate;

  const maturityDate = new Date(displayMaturityDate);

  const yearsDifference = differenceInYears(maturityDate, issueDate);
  const monthsDifference = differenceInMonths(maturityDate, issueDate);
  const interestRate = (promissoryNote.interestRate * 100).toFixed(2) + '% ';
  const fixedMaturity = promissoryNote.noFixedMaturity
    ? ' Non-fixed, '
    : ' Fixed, ';
  const yearsDifferenceText =
    yearsDifference > 0 ? yearsDifference + '-year' : null;

  const monthsDifferenceText =
    yearsDifference === 0 && monthsDifference > 0
      ? monthsDifference + '-month'
      : null;
  return (
    <InfoCard
      title={title}
      className={classes.wrapper}
      contentClassName={classes.content}>
      <div className={classes.container}>
        <div className={classes.maturityDateContainer}>
          <Typography
            color="textSecondary"
            variant="body2"
            className={classes.maturityDate}>
            Maturity date
          </Typography>
          <Typography
            color="textSecondary"
            className={classes.maturityDateAmount}
            data-private>
            {formatDate(displayMaturityDate)}
          </Typography>
        </div>
        <div>
          <Typography
            color="textSecondary"
            variant="body2"
            className={classes.interestRate}>
            Interest rate
          </Typography>
          <Typography
            color="textSecondary"
            className={classes.interestRateAmount}
            data-private>
            {interestRate}
            {fixedMaturity}
            {yearsDifferenceText}
            {monthsDifferenceText}
          </Typography>
        </div>
      </div>
    </InfoCard>
  );
};

export default AssetInfoCard;
